import { useEffect, useState } from "react";
import { debounce } from "../../utils";

export type UseTruncatedOptions<T> = {
  elementRef: React.RefObject<T | null>;
  onChange?: (isTruncated: boolean) => void;
  resizeDetectDelay?: number;
};

export type UseTruncatedResult = boolean;

const useTruncated = <T extends HTMLElement = any>({
  elementRef,
  onChange,
  resizeDetectDelay = 150
}: UseTruncatedOptions<T>): UseTruncatedResult => {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const checkTruncate = debounce(() => {
      const truncated = element.scrollWidth > element.clientWidth;
      setIsTruncated((prev) => {
        if (prev !== truncated) {
          onChange?.(truncated);
        }
        return truncated;
      });
    }, resizeDetectDelay);

    checkTruncate();

    const observer = new ResizeObserver(checkTruncate);
    observer.observe(element);
    window.addEventListener("resize", checkTruncate);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkTruncate);
    };
  }, [elementRef, onChange, resizeDetectDelay]);

  return isTruncated;
};

export default useTruncated;
